import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RegistrationForm from './components/RegistrationForm';
import TrustSignals from './components/TrustSignals';
import RegistrationProgress from './components/RegistrationProgress';

const Register = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState(1);
  const navigate = useNavigate();

  const handleRegistration = async (formData) => {
    setIsLoading(true);
    
    try {
      // Mock registration API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock successful registration
      console.log('Registration data:', formData);
      
      // Move to verification step
      setRegistrationStep(2);
      
      // Show success message and redirect after delay
      setTimeout(() => {
        navigate('/customer-dashboard', { 
          state: { 
            message: 'Registration successful! Please check your email for verification.',
            user: {
              name: `${formData?.firstName} ${formData?.lastName}`,
              email: formData?.email,
              role: formData?.accountType === 'enterprise' ? 'customer' : 'customer',
              accountType: formData?.accountType,
              company: formData?.companyName || null
            }
          }
        });
      }, 3000);
      
    } catch (error) {
      console.error('Registration failed:', error);
      // Handle registration error
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Register - TechTribes Commerce</title>
        <meta name="description" content="Create your TechTribes account to access Uganda's premier ICT solutions and request quotes for your business needs." />
        <meta name="keywords" content="register, signup, account, ICT solutions, Uganda, business technology" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="bg-card border-b border-border shadow-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-smooth">
                <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                  <Icon name="Zap" size={20} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">TechTribes</span>
              </Link>
              
              <div className="flex items-center space-x-4">
                <span className="text-sm text-muted-foreground">Already have an account?</span>
                <Link to="/login">
                  <Button variant="outline" size="sm">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Registration Form */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-lg shadow-moderate border border-border p-6 lg:p-8">
                {/* Header */}
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-foreground mb-2">
                    Create Your Account
                  </h1>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Join TechTribes to access Uganda's leading ICT solutions and streamline your technology procurement process.
                  </p>
                </div>

                {/* Mission Statement */}
                <div className="bg-primary/5 rounded-lg p-4 mb-8 border border-primary/20">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Target" size={18} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground mb-1">
                        Our Mission
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-primary">"Innovate, Connect, and Transform"</strong> – 
                        Empowering Uganda's businesses with cutting-edge ICT solutions that drive growth and digital transformation.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Registration Form */}
                <RegistrationForm 
                  onSubmit={handleRegistration}
                  isLoading={isLoading}
                />

                {/* Footer Links */}
                <div className="mt-8 pt-6 border-t border-border text-center">
                  <p className="text-sm text-muted-foreground">
                    By creating an account, you agree to our{' '}
                    <Link to="/terms" className="text-primary hover:text-primary/80 font-medium">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-primary hover:text-primary/80 font-medium">
                      Privacy Policy
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Trust Signals & Progress */}
            <div className="space-y-6">
              {/* Registration Progress */}
              <RegistrationProgress 
                currentStep={registrationStep}
                totalSteps={3}
              />

              {/* Trust Signals */}
              <TrustSignals />

              {/* Quick Stats */}
              <div className="bg-card rounded-lg p-6 border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                  <Icon name="TrendingUp" size={20} className="mr-2 text-primary" />
                  Why Choose TechTribes?
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary mb-1">500+</div>
                    <div className="text-xs text-muted-foreground">Projects Completed</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary mb-1">150+</div>
                    <div className="text-xs text-muted-foreground">Happy Clients</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary mb-1">24/7</div>
                    <div className="text-xs text-muted-foreground">Support Available</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold text-primary mb-1">5+</div>
                    <div className="text-xs text-muted-foreground">Years Experience</div>
                  </div>
                </div>
              </div>

              {/* Contact Support */}
              <div className="bg-accent/5 rounded-lg p-6 border border-accent/20">
                <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="HelpCircle" size={20} className="mr-2 text-accent" />
                  Need Help?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Our support team is here to help you get started with your TechTribes account.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Phone" size={14} className="text-accent" />
                    <span className="text-foreground">+256 700 123 456</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Mail" size={14} className="text-accent" />
                    <span className="text-foreground">support@techtribes.ug</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-accent" />
                    <span className="text-foreground">Mon-Fri: 8AM-6PM EAT</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-card border-t border-border mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} TechTribes Creations Limited. All rights reserved.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Registered in Uganda | UCC Licensed ICT Service Provider
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Register;